import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Download, Trash2, X, DatabaseBackup, Play } from "lucide-react";

const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function BackupsModal({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backups");
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/backups/run");
      setItems(data);
      toast.success("Yedek alındı");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async (b) => {
    try {
      const res = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", b.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const del = async (b) => {
    if (!window.confirm(`"${b.filename}" yedeği silinsin mi?`)) return;
    try {
      await api.delete(`/backups/${b.id}`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="backups-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><DatabaseBackup size={18} /> Otomatik Yedekler</h2>
            <p className="text-xs text-slate-500 mt-0.5">Her gece 03:00'te otomatik yedek alınır; son 14 yedek saklanır.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={runNow} disabled={busy} className="h-8 text-xs bg-blue-600 hover:bg-blue-700" data-testid="backup-run-now-btn">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} <span className="ml-1">Şimdi Yedek Al</span>
            </Button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="backups-close-btn"><X size={20} /></button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto">
          {loading ? (
            <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-slate-500" data-testid="backups-empty">Henüz otomatik yedek yok.</div>
          ) : (
            <div className="space-y-2" data-testid="backups-list">
              {items.map((b) => (
                <div key={b.id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3" data-testid={`backup-item-${b.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm font-semibold text-slate-900 truncate">{b.filename}</div>
                    <div className="text-xs text-slate-500">
                      {new Date(b.created_at).toLocaleString("tr-TR")} · {fmtSize(b.size)} · <span className={`font-semibold ${b.trigger === "nightly" ? "text-emerald-600" : "text-indigo-600"}`}>{b.trigger === "nightly" ? "Gece otomatik" : "Elle"}</span>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => download(b)} className="h-8 text-xs" data-testid={`backup-download-${b.id}`}>
                    <Download size={13} /> <span className="ml-1">İndir</span>
                  </Button>
                  <button onClick={() => del(b)} className="text-slate-300 hover:text-rose-600" title="Sil" data-testid={`backup-delete-${b.id}`}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
